import type {
  Patient,
  Appointment,
  Invoice,
  InventoryItem,
  TreatmentRecord,
} from '@clinic-os/shared/types';

export const DEMO_CLINIC_ID = 'clinic_demo_001';

export const DEMO_PATIENTS: Patient[] = [
  {
    id: 'p1',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'Maria',
    lastName: 'Garcia',
    email: 'maria.garcia@email.com',
    phone: '+66 81 234 5678',
    dateOfBirth: '1985-03-15',
    allergies: ['Penicillin', 'Codeine'],
    medications: [],
    notes: 'Regular dental patient. Prefers morning appointments.',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-03-20T14:30:00Z',
  },
  {
    id: 'p2',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'James',
    lastName: 'Wilson',
    email: 'j.wilson@email.com',
    phone: '+66 82 345 6789',
    dateOfBirth: '1972-08-22',
    allergies: [],
    medications: ['Metformin 500mg', 'Lisinopril 10mg'],
    notes: 'Type 2 diabetic. Blood sugar should be checked before procedures.',
    createdAt: '2024-02-10T09:00:00Z',
    updatedAt: '2024-03-18T11:00:00Z',
  },
  {
    id: 'p3',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'Sarah',
    lastName: 'Chen',
    email: 'sarah.chen@email.com',
    phone: '+66 83 456 7890',
    dateOfBirth: '1990-11-08',
    allergies: ['Latex'],
    medications: [],
    notes: 'Works in tech. Weekend appointments preferred.',
    createdAt: '2024-03-01T15:00:00Z',
    updatedAt: '2024-03-20T09:00:00Z',
  },
  {
    id: 'p4',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'Ahmed',
    lastName: 'Hassan',
    email: 'ahmed.h@email.com',
    phone: '+66 84 567 8901',
    dateOfBirth: '1978-05-30',
    allergies: [],
    medications: [],
    notes: 'New patient. First visit completed root canal assessment.',
    createdAt: '2024-03-18T10:00:00Z',
    updatedAt: '2024-03-18T12:00:00Z',
  },
  {
    id: 'p5',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'Lisa',
    lastName: 'Park',
    email: 'lisa.park@email.com',
    phone: '+66 85 678 9012',
    dateOfBirth: '1995-01-20',
    allergies: ['Amoxicillin'],
    medications: [],
    notes: 'Regular cleaning every 6 months.',
    createdAt: '2024-01-20T11:00:00Z',
    updatedAt: '2024-03-15T16:00:00Z',
  },
  {
    id: 'p6',
    clinicId: DEMO_CLINIC_ID,
    firstName: 'Robert',
    lastName: 'Tanaka',
    email: 'r.tanaka@email.com',
    phone: '+66 86 789 0123',
    dateOfBirth: '1965-12-05',
    allergies: ['Ibuprofen'],
    medications: ['Warfarin 5mg'],
    notes: 'On blood thinner. Coordinate with cardiologist before extractions.',
    createdAt: '2024-02-05T09:00:00Z',
    updatedAt: '2024-03-10T14:00:00Z',
  },
];

const today = new Date();
const tomorrow = new Date(today.getTime() + 86400000);
const yesterday = new Date(today.getTime() - 86400000);

export const DEMO_APPOINTMENTS: Appointment[] = [
  {
    id: 'a1',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p1',
    practitionerId: 'dr1',
    status: 'confirmed',
    scheduledAt: new Date(today.setHours(9, 0, 0, 0)).toISOString(),
    durationMinutes: 60,
    type: 'Root Canal',
    notes: 'Lower right molar — may need referral to endodontist',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a2',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p2',
    practitionerId: 'dr1',
    status: 'scheduled',
    scheduledAt: new Date(today.setHours(10, 30, 0, 0)).toISOString(),
    durationMinutes: 30,
    type: 'Checkup',
    notes: '6-month routine check. Check blood sugar beforehand.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a3',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p3',
    practitionerId: 'dr1',
    status: 'confirmed',
    scheduledAt: new Date(today.setHours(11, 30, 0, 0)).toISOString(),
    durationMinutes: 45,
    type: 'Filling',
    notes: 'Composite filling on #14 distal',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a4',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p4',
    practitionerId: 'dr1',
    status: 'scheduled',
    scheduledAt: new Date(today.setHours(14, 0, 0, 0)).toISOString(),
    durationMinutes: 45,
    type: 'Extraction',
    notes: 'Wisdom tooth extraction evaluation',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a5',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p5',
    practitionerId: 'dr1',
    status: 'confirmed',
    scheduledAt: new Date(today.setHours(15, 30, 0, 0)).toISOString(),
    durationMinutes: 30,
    type: 'Cleaning',
    notes: 'Regular 6-month cleaning',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a6',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p6',
    practitionerId: 'dr1',
    status: 'scheduled',
    scheduledAt: new Date(tomorrow.setHours(9, 0, 0, 0)).toISOString(),
    durationMinutes: 60,
    type: 'Consultation',
    notes: 'Warfarin coordination needed before any surgical procedure',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a7',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p1',
    practitionerId: 'dr1',
    status: 'completed',
    scheduledAt: new Date(yesterday.setHours(10, 0, 0, 0)).toISOString(),
    durationMinutes: 30,
    type: 'Follow-up',
    notes: 'Post-root canal check',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'a8',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p3',
    practitionerId: 'dr1',
    status: 'no_show',
    scheduledAt: new Date(yesterday.setHours(14, 0, 0, 0)).toISOString(),
    durationMinutes: 30,
    type: 'Checkup',
    notes: 'Patient did not show — call to reschedule',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const DEMO_INVOICES: Invoice[] = [
  {
    id: 'inv1',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p1',
    invoiceNumber: 'INV-2026-001',
    status: 'paid',
    items: [
      { id: 'i1', description: 'Root Canal Treatment', quantity: 1, unitPrice: 350, total: 350 },
      { id: 'i2', description: 'X-Ray (periapical)', quantity: 2, unitPrice: 25, total: 50 },
    ],
    subtotal: 400,
    tax: 28,
    total: 428,
    paidAt: '2026-03-15T10:00:00Z',
    createdAt: '2026-03-01T09:00:00Z',
    updatedAt: '2026-03-15T10:00:00Z',
    notes: '',
  },
  {
    id: 'inv2',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p2',
    invoiceNumber: 'INV-2026-002',
    status: 'sent',
    items: [
      { id: 'i3', description: 'Routine Checkup', quantity: 1, unitPrice: 100, total: 100 },
      { id: 'i4', description: 'Cleaning', quantity: 1, unitPrice: 80, total: 80 },
    ],
    subtotal: 180,
    tax: 12.6,
    total: 192.6,
    dueDate: '2026-03-28T00:00:00Z',
    createdAt: '2026-03-10T11:00:00Z',
    updatedAt: '2026-03-10T11:00:00Z',
    notes: '',
  },
  {
    id: 'inv3',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p3',
    invoiceNumber: 'INV-2026-003',
    status: 'overdue',
    items: [
      { id: 'i5', description: 'Composite Filling', quantity: 2, unitPrice: 120, total: 240 },
      { id: 'i6', description: 'X-Ray', quantity: 1, unitPrice: 25, total: 25 },
    ],
    subtotal: 265,
    tax: 18.55,
    total: 283.55,
    dueDate: '2026-03-10T00:00:00Z',
    createdAt: '2026-02-28T14:00:00Z',
    updatedAt: '2026-03-10T14:00:00Z',
    notes: 'Sent reminder on Mar 15',
  },
  {
    id: 'inv4',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p4',
    invoiceNumber: 'INV-2026-004',
    status: 'draft',
    items: [
      { id: 'i7', description: 'Extraction (Simple)', quantity: 1, unitPrice: 150, total: 150 },
    ],
    subtotal: 150,
    tax: 10.5,
    total: 160.5,
    createdAt: '2026-03-18T15:00:00Z',
    updatedAt: '2026-03-18T15:00:00Z',
    notes: '',
  },
  {
    id: 'inv5',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p5',
    invoiceNumber: 'INV-2026-005',
    status: 'paid',
    items: [
      { id: 'i8', description: 'Teeth Cleaning', quantity: 1, unitPrice: 80, total: 80 },
    ],
    subtotal: 80,
    tax: 5.6,
    total: 85.6,
    paidAt: '2026-03-15T16:00:00Z',
    createdAt: '2026-03-15T16:30:00Z',
    updatedAt: '2026-03-15T16:30:00Z',
    notes: '',
  },
];

export const DEMO_INVENTORY: InventoryItem[] = [
  {
    id: 'inv1',
    clinicId: DEMO_CLINIC_ID,
    name: 'Lidocaine 5%',
    sku: 'LIDO-5',
    category: 'Anesthetics',
    quantity: 8,
    unit: 'vials',
    lowStockThreshold: 10,
    expiryDate: '2027-06-30T00:00:00Z',
    costPerUnit: 12.5,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv2',
    clinicId: DEMO_CLINIC_ID,
    name: 'Disposable Gloves (M)',
    sku: 'GLV-M',
    category: 'Supplies',
    quantity: 250,
    unit: 'boxes',
    lowStockThreshold: 50,
    costPerUnit: 8,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv3',
    clinicId: DEMO_CLINIC_ID,
    name: 'Dental Floss',
    sku: 'DF-001',
    category: 'Supplies',
    quantity: 45,
    unit: 'packs',
    lowStockThreshold: 20,
    costPerUnit: 3.5,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv4',
    clinicId: DEMO_CLINIC_ID,
    name: 'Filling Material (Composite)',
    sku: 'FIL-CMP',
    category: 'Materials',
    quantity: 12,
    unit: 'units',
    lowStockThreshold: 5,
    costPerUnit: 35,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv5',
    clinicId: DEMO_CLINIC_ID,
    name: 'Disposable Syringes (3ml)',
    sku: 'SYR-3ML',
    category: 'Supplies',
    quantity: 18,
    unit: 'boxes',
    lowStockThreshold: 15,
    costPerUnit: 5,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv6',
    clinicId: DEMO_CLINIC_ID,
    name: 'Infection Control Gloves (L)',
    sku: 'GLV-L',
    category: 'Supplies',
    quantity: 120,
    unit: 'boxes',
    lowStockThreshold: 30,
    costPerUnit: 9,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv7',
    clinicId: DEMO_CLINIC_ID,
    name: 'Sodium Hypochlorite 3%',
    sku: 'NAOCL-3',
    category: 'Endodontics',
    quantity: 6,
    unit: 'bottles',
    lowStockThreshold: 5,
    costPerUnit: 18,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
  {
    id: 'inv8',
    clinicId: DEMO_CLINIC_ID,
    name: 'Interim Restoration Material',
    sku: 'IRM-001',
    category: 'Materials',
    quantity: 3,
    unit: 'kits',
    lowStockThreshold: 3,
    costPerUnit: 22,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-03-20T09:00:00Z',
  },
];

export const DEMO_TREATMENTS: TreatmentRecord[] = [
  {
    id: 't1',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p1',
    appointmentId: 'a7',
    treatmentType: 'Follow-up',
    description: 'Post-root canal review. Patient reports no pain. Healing well.',
    icd10Codes: ['K04.0'],
    cptCodes: ['D0140'],
    notes: 'Schedule 6-month review',
    createdAt: new Date(yesterday.setHours(11, 0, 0, 0)).toISOString(),
  },
  {
    id: 't2',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p2',
    appointmentId: 'a2',
    treatmentType: 'Checkup',
    description: 'Routine 6-month checkup. No cavities. Minor gingivitis noted. Recommend improved flossing.',
    icd10Codes: ['K05.1'],
    cptCodes: ['D0120'],
    notes: 'Blood sugar: 118 mg/dL (fasting). OK for treatment.',
    createdAt: new Date(today.setHours(11, 0, 0, 0)).toISOString(),
  },
  {
    id: 't3',
    clinicId: DEMO_CLINIC_ID,
    patientId: 'p5',
    appointmentId: 'a5',
    treatmentType: 'Prophylaxis',
    description: 'Professional cleaning. Light staining removed. Fluoride treatment applied.',
    icd10Codes: ['Z46.3'],
    cptCodes: ['D1110', 'D1208'],
    notes: 'Patient tolerate procedure well.',
    createdAt: new Date(today.setHours(16, 0, 0, 0)).toISOString(),
  },
];
